// The timeline — ports app/(privado)/timeline/page.tsx.
//
// Criar e editar um marco acontece aqui, no trilho: "+ Novo marco" abre o
// formulário por baixo do cabeçalho, e "Editar" abre-o no lugar do próprio
// marco. É o padrão do mapa e do calendário — antes eram duas páginas à parte
// (/adicionar e /marco/:id/editar).

import { useState } from "react";
import type { Milestone as Marco } from "@historia/shared/types";
import type { MilestoneWithPhotosSrc } from "@historia/shared/contracts";
import { categoryInfo } from "@historia/shared/categories";
import { longDate } from "@historia/shared/dates";
import { emptyStates } from "@historia/shared/content";
import { Flourish } from "@/components/ui/Flourish";
import { BotaoApagar } from "@/components/ui/BotaoApagar";
import { Consulta } from "@/components/ui/EstadoConsulta";
import { useTitle } from "@/components/ui/useTitle";
import { FormMarco } from "@/components/timeline/FormMarco";
import { useTimeline } from "@/api/queries";
import { AFTER_MILESTONE_CHANGE, deleteMilestone } from "@/api/mutations";
import { useAccao } from "@/api/useAccao";

export function Timeline() {
  useTitle("O nosso trilho");
  const query = useTimeline();

  const [aCriar, setACriar] = useState(false);
  const [aEditar, setAEditar] = useState<Marco | null>(null);

  const [, apagar] = useAccao((id: string) => deleteMilestone(id), {
    invalidate: AFTER_MILESTONE_CHANGE,
  });

  function fecharForm() {
    setACriar(false);
    setAEditar(null);
  }

  return (
    <Consulta query={query}>
      {(marcos) => (
        <main className="mx-auto max-w-2xl px-6 py-16">
          <header className="text-center">
            <p className="font-display text-lg italic text-ouro-600">O nosso trilho</p>
            <h1 className="mt-2 font-display text-4xl text-tinta">A nossa linha do tempo</h1>
            <div className="my-6">
              <Flourish />
            </div>

            {!aCriar && aEditar === null && (
              <button
                type="button"
                onClick={() => setACriar(true)}
                className="rounded-full bg-ouro px-6 py-3 text-sm font-semibold text-creme-50 shadow-[0_10px_28px_-10px_rgba(201,162,39,0.65)] transition-all hover:-translate-y-0.5 hover:bg-ouro-600"
              >
                + Novo marco
              </button>
            )}
          </header>

          {aCriar && (
            <div className="mt-6">
              <FormMarco aoTerminar={fecharForm} />
            </div>
          )}

          {marcos.length === 0 ? (
            <div className="mt-10 text-center">
              <p className="mx-auto max-w-prose leading-relaxed text-tinta-700">
                {emptyStates.timeline}
              </p>
            </div>
          ) : (
            <ol className="relative mt-6 border-l border-ouro/25 pl-8">
              {marcos.map((marco: MilestoneWithPhotosSrc) => {
                const cat = categoryInfo(marco.categoria);
                const emEdicao = aEditar?.id === marco.id;

                return (
                  <li key={marco.id} className="relative mb-12 last:mb-0">
                    {/* Marker on the line */}
                    <span
                      aria-hidden
                      className="absolute -left-[2.6rem] flex h-9 w-9 items-center justify-center rounded-full bg-creme text-base shadow-sm ring-4 ring-creme"
                      style={{ border: `2px solid ${cat.color}` }}
                    >
                      {cat.icon}
                    </span>

                    {emEdicao ? (
                      // `key` força um formulário novo ao trocar de marco — sem
                      // isto, o React reaproveitava o estado do marco anterior.
                      <FormMarco key={marco.id} marco={aEditar} aoTerminar={fecharForm} />
                    ) : (
                      <article className="rounded-3xl border border-ouro/15 bg-creme-50/70 p-5 shadow-sm transition-shadow hover:shadow-md">
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <h2 className="font-display text-2xl text-tinta">{marco.titulo}</h2>
                          <span
                            className="rounded-full px-2.5 py-0.5 text-xs font-semibold"
                            style={{ backgroundColor: `${cat.color}22`, color: cat.color }}
                          >
                            {cat.label}
                          </span>
                        </div>

                        <p className="mt-1 text-sm text-tinta-700/80">
                          {longDate(marco.data)}
                          {marco.local ? ` · ${marco.local}` : ""}
                        </p>

                        {marco.descricao && (
                          <p className="mt-3 leading-relaxed text-tinta-700">{marco.descricao}</p>
                        )}

                        {marco.fotos.length > 0 && (
                          <div className="mt-4 grid grid-cols-3 gap-2">
                            {marco.fotos.map((foto) =>
                              foto.src ? (
                                <div
                                  key={foto.id}
                                  className="relative aspect-square overflow-hidden rounded-xl"
                                >
                                  {/* next/image `fill` → absolute inset-0. The parent
                                      already has `relative aspect-square`, so the
                                      layout is identical. */}
                                  <img
                                    src={foto.src}
                                    alt={foto.legenda ?? marco.titulo}
                                    loading="lazy"
                                    decoding="async"
                                    className="absolute inset-0 h-full w-full object-cover"
                                  />
                                </div>
                              ) : null,
                            )}
                          </div>
                        )}

                        <div className="mt-4 flex items-center justify-end gap-3 border-t border-ouro/10 pt-3">
                          <button
                            type="button"
                            onClick={() => {
                              setACriar(false);
                              setAEditar(marco);
                            }}
                            className="text-xs font-semibold text-ouro-600 underline-offset-4 transition-colors hover:text-ouro hover:underline"
                          >
                            Editar
                          </button>
                          <BotaoApagar
                            accao={apagar}
                            id={marco.id}
                            etiqueta="Apagar"
                            confirmacao={`Apagar "${marco.titulo}"? As fotos ligadas ficam no álbum (apenas desligadas).`}
                          />
                        </div>
                      </article>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </main>
      )}
    </Consulta>
  );
}
